import type { Task, TaskProgress } from '../types';

export type DueState = 'overdue' | 'today' | 'upcoming' | 'done';
export type ApprovalOutcome = { status: 'archived' | 'rejected'; approvalStatus: 'approved' | 'rejected'; shouldArchive: boolean };

const RIYADH = 'Asia/Riyadh';
const CLOSED_TASK_STATUSES: Task['status'][] = ['completed', 'archived', 'cancelled'];

export function approvalOutcome(approved: boolean): ApprovalOutcome {
  return approved
    ? { status: 'archived', approvalStatus: 'approved', shouldArchive: true }
    : { status: 'rejected', approvalStatus: 'rejected', shouldArchive: false };
}

function dateKey(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: RIYADH, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

export function daysRemaining(due: Date, now = new Date()): number {
  const dueKey = dateKey(due);
  const nowKey = dateKey(now);
  const utcDue = Date.parse(`${dueKey}T00:00:00Z`);
  const utcNow = Date.parse(`${nowKey}T00:00:00Z`);
  return Math.round((utcDue - utcNow) / 86_400_000);
}

export function isTaskClosed(status: Task['status']): boolean {
  return CLOSED_TASK_STATUSES.includes(status);
}

export function isTaskOverdue(task: Pick<Task, 'status' | 'currentDueAt'>, now = new Date()): boolean {
  return !isTaskClosed(task.status) && daysRemaining(task.currentDueAt.toDate(), now) < 0;
}

export function formatAssigneeNames(names: string[]): string {
  if (!names.length) return 'دون مكلّف';
  if (names.length <= 2) return names.join('، ');
  return `${names.slice(0, 2).join('، ')} +${names.length - 2}`;
}

export function taskWorkload(uid: string, tasks: Array<{ data: Task }>, now = new Date()): { active: number; overdue: number; highPriority: number } {
  const active = tasks.filter((task) => task.data.assigneeIds.includes(uid) && !isTaskClosed(task.data.status));
  return {
    active: active.length,
    overdue: active.filter((task) => isTaskOverdue(task.data, now)).length,
    highPriority: active.filter((task) => ['high', 'urgent'].includes(task.data.priority)).length,
  };
}

export function dueState(due: Date, completed: boolean, now = new Date()): DueState {
  if (completed) return 'done';
  const days = daysRemaining(due, now);
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  return 'upcoming';
}

export interface TaskTimeState {
  remainingDays: number;
  remainingShare: number;
  state: DueState;
  label: string;
}

export function taskTimeState(created: Date, due: Date, completed: boolean, now = new Date()): TaskTimeState {
  const totalDays = Math.max(1, daysRemaining(due, created));
  const remainingDays = daysRemaining(due, now);
  const state = dueState(due, completed, now);
  const remainingShare = completed ? 0 : Math.max(0, Math.min(100, Math.round((remainingDays / totalDays) * 100)));
  const remainingText = remainingDays === 1 ? 'يوم واحد' : remainingDays === 2 ? 'يومان' : remainingDays >= 3 && remainingDays <= 10 ? `${remainingDays} أيام` : `${remainingDays} يومًا`;
  const label = completed
    ? 'تم إنجاز المهمة'
    : remainingDays < 0
      ? `متأخرة ${Math.abs(remainingDays)} يومًا`
      : remainingDays === 0
        ? 'الموعد التنفيذي اليوم'
        : `متبقي ${remainingText} على الموعد التنفيذي`;
  return { remainingDays, remainingShare, state, label };
}

export function allAssigneesDone(progress: TaskProgress[], assigneeIds: string[]): boolean {
  return assigneeIds.length > 0 && assigneeIds.every((id) => progress.some((item) => item.userId === id && item.percent === 100));
}

export function complianceRate(totalDue: number, completedOnTime: number): number {
  return totalDue <= 0 ? 0 : Math.round((completedOnTime / totalDue) * 100);
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { timeZone: RIYADH, day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}
